// src/components/security/ui.ts
/**
 * The visual vocabulary of the security page, in one place so the four cards look like one screen.
 *
 * WHY THESE ARE COPIES AND NOT IMPORTS. This app has no shared UI primitives — every page declares
 * its own styled-components at the bottom of its own file, and `Campaigns.tsx` and `Invoicing.tsx`
 * each carry a near-identical `Card`/`Primary`/`Banner` set that has already drifted apart in the
 * small numbers. Importing one page's private styles into another would couple two admin screens
 * that are otherwise independent, and would make any tweak to the campaigns page a change to this
 * one. So the security folder keeps its own copy, taken from `Campaigns.tsx:846-1145` (the newer of
 * the two vocabularies) with Invoicing's page frame (`Container`/`Header`/`BackButton`/`Heading`)
 * and its table cells, and the sizes adjusted where a KPI here holds a word rather than a number.
 *
 * This is a `.ts` file, not `.tsx`: there is no JSX in it, only styled-component declarations.
 *
 * Transient props are prefixed with `$` throughout (styled-components 6 forwards anything else to
 * the DOM, and React then warns about an unknown attribute on every render).
 */
import styled from 'styled-components';

/* ---------------- page frame ---------------- */

/** 1100px, matching the invoicing page. `App.tsx` renders `/bezpecnost` full-width, so the page is
 *  responsible for its own measure. */
export const Container = styled.div`
    padding: 20px;
    width: 100%;
    max-width: 1100px;
    margin: 0 auto;
`;

export const Header = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 8px;
`;

export const BackButton = styled.button`
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    margin-right: 10px;
`;

export const Heading = styled.h2`
    margin: 0;
    font-size: 24px;
`;

/** The sentence under the page title. Sits where the header's own bottom margin used to be. */
export const Lead = styled.p`
    margin: 0 0 22px;
    font-size: 13.5px;
    line-height: 1.6;
    color: #777;
`;

/* ---------------- KPI strip ---------------- */

export const Kpis = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
    gap: 12px;
    margin-bottom: 20px;
`;

export const Kpi = styled.div`
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.08);
    padding: 15px 17px;
`;

export const KpiLabel = styled.div`
    font-size: 12px;
    color: #777;
    margin-bottom: 5px;
`;

/**
 * 20px rather than the campaigns page's 27px: three of the four values on this strip are words
 * ("Neoverená", "vypnuté"), not two-digit counts, and 27px wraps them inside a 170px column.
 */
export const KpiValue = styled.div<{ $tone?: 'ok' | 'warn' | 'plain' }>`
    font-size: 20px;
    font-weight: 700;
    line-height: 1.3;
    color: ${(p) => (p.$tone === 'ok' ? '#256029' : p.$tone === 'warn' ? '#8a5300' : '#141210')};
`;

/* ---------------- cards ---------------- */

export const Card = styled.div`
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.08);
    padding: 18px 20px;
    margin-bottom: 16px;
`;

export const CardTitle = styled.h2`
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.13em;
    text-transform: uppercase;
    color: #75716b;
    margin: 0 0 14px;
`;

export const Field = styled.div`
    margin-bottom: 14px;
`;

export const Label = styled.label`
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #141210;
    margin-bottom: 6px;
`;

export const Input = styled.input`
    width: 100%;
    max-width: 320px;
    font: inherit;
    font-size: 14px;
    padding: 10px 12px;
    border: 1px solid #ddd;
    border-radius: 5px;

    &:focus {
        outline: none;
        border-color: #f56607;
        box-shadow: 0 0 0 3px rgba(245, 102, 7, 0.13);
    }

    &:disabled {
        background: #f5f5f5;
        color: #777;
    }
`;

/** A field that holds a code: wide letter spacing so a mistyped digit is visible before it is sent. */
export const CodeInput = styled(Input)`
    max-width: 190px;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 19px;
    letter-spacing: 0.24em;
`;

export const Hint = styled.p`
    font-size: 12.5px;
    line-height: 1.65;
    color: #777;
    margin: 8px 0 0;
`;

export const WarnBox = styled.div`
    background: #fff4e5;
    color: #8a5300;
    border-radius: 6px;
    padding: 10px 13px;
    font-size: 13px;
    line-height: 1.6;
    margin-bottom: 14px;
`;

export const Row = styled.div`
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    align-items: center;
`;

/** The separated area a destructive action lives in, so a typed confirmation never reads as part of
 *  the form above it. */
export const Confirm = styled.div`
    border-top: 1px solid #eee;
    margin-top: 16px;
    padding-top: 16px;
`;

/* ---------------- buttons ---------------- */

export const Secondary = styled.button`
    font: inherit;
    font-size: 14px;
    font-weight: 600;
    padding: 11px 20px;
    border-radius: 5px;
    border: 1px solid #ddd;
    background: #fff;
    color: #141210;
    cursor: pointer;

    &:disabled {
        opacity: 0.6;
        cursor: default;
    }
`;

export const Primary = styled(Secondary)`
    background: #f56607;
    border-color: #f56607;
    color: #fff;
`;

export const Danger = styled(Secondary)`
    border-color: #e3b3b3;
    color: #a11;
`;

export const LinkButton = styled.button`
    background: none;
    border: none;
    color: #f56607;
    font: inherit;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    padding: 0;
    text-align: left;

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
`;

/* ---------------- feedback ---------------- */

/** Red by default, green with `$ok`, amber with `$warn`. All three are inline: this codebase has no
 *  toast system, and D10 keeps it that way. */
export const Banner = styled.p<{ $ok?: boolean; $warn?: boolean }>`
    background: ${(p) => (p.$warn ? '#fff4e5' : p.$ok ? '#eef7ee' : '#fdecec')};
    color: ${(p) => (p.$warn ? '#8a5300' : p.$ok ? '#256029' : '#a11')};
    border-radius: 8px;
    padding: 12px 16px;
    font-size: 13.5px;
    line-height: 1.6;
    margin: 0 0 16px;
`;

/* ---------------- table ---------------- */

export const TableWrap = styled.div`
    overflow-x: auto;
`;

export const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th {
        text-align: left;
        font-size: 11px;
        letter-spacing: 0.09em;
        text-transform: uppercase;
        color: #777;
        padding: 0 0 9px;
        border-bottom: 1px solid #eee;
        white-space: nowrap;
    }

    td {
        padding: 11px 12px 11px 0;
        border-bottom: 1px solid #f2f2f0;
        vertical-align: top;
    }

    th:first-child,
    td:first-child {
        padding-left: 0;
    }
`;

export const StateCell = styled.td`
    padding: 26px 0;
    text-align: center;
    color: #777;
`;

export const Muted = styled.div`
    color: #888;
    font-size: 12px;
    margin-top: 2px;
`;

export const Chip = styled.span<{ $tone?: 'ok' | 'warn' | 'info' }>`
    display: inline-block;
    padding: 3px 9px;
    border-radius: 999px;
    font-size: 11.5px;
    font-weight: 600;
    white-space: nowrap;
    background: ${(p) => (p.$tone === 'ok' ? '#e6f4ea' : p.$tone === 'warn' ? '#fff4e5' : '#eef1f6')};
    color: ${(p) => (p.$tone === 'ok' ? '#1e7e34' : p.$tone === 'warn' ? '#8a5300' : '#39506e')};
`;

/* ---------------- secrets ---------------- */

/**
 * A secret meant to be read and copied by hand: the Base32 TOTP key and each recovery code.
 * `user-select: all` makes one click select the whole value, which matters on the one screen where
 * a half-selected secret is a lockout.
 */
export const Mono = styled.code`
    display: inline-block;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 14px;
    letter-spacing: 0.06em;
    background: #f4f5f6;
    border-radius: 4px;
    padding: 7px 10px;
    user-select: all;
    word-break: break-all;
`;

/** The ten recovery codes. Two columns so the whole set is visible without scrolling, one on a
 *  phone. */
export const Codes = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    margin: 12px 0 14px;

    @media (max-width: 520px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

/** The "I have saved these" acknowledgement. A label wrapping its own checkbox, so the text is part
 *  of the hit target. */
export const CheckRow = styled.label`
    display: flex;
    align-items: flex-start;
    gap: 9px;
    font-size: 13.5px;
    line-height: 1.5;
    color: #141210;
    cursor: pointer;
    margin-bottom: 14px;

    input {
        margin: 2px 0 0;
        width: 16px;
        height: 16px;
        cursor: pointer;
    }
`;
